import React from 'react';

import { setIn } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import { CronFrequencyEnum, CustomFrequencyEnum } from '@/types/schema';

import { FrequencyComponentProps } from '../../../types';
import TimeOfDay from '../../InputComponents/TimeOfDay';
import WeeksOfMonthInput from '../../InputComponents/WeeksOfMonth';
import IntervalInput from './CustomInputComponents/IntervalInput';
import MultiWeekday from './CustomInputComponents/MultiWeekday';
import CustomFrequencyInput from './CustomFrequencyInput';

const CustomWeek: React.FC<FrequencyComponentProps> = React.memo((props) => {
  const {
    value,
    onChange,
    hideTimeSelection,
    disabled,
    allowedCustomFrequencies,
    errorConfig,
    size,
    overrides: overridesProp,
  } = props;
  const { t } = useTranslation();

  const onWeekOfMonthChange: FrequencyComponentProps['onChange'] = React.useCallback(
    ({ weeksOfMonth }) => {
      onChange(setIn(value, ['custom', 'weeksOfMonth'], weeksOfMonth));
    },
    [onChange, value],
  );

  const overrides = overridesProp?.[CronFrequencyEnum.Custom]?.[CustomFrequencyEnum.Month];

  return (
    <Stack direction="col" gapY={16}>
      <Stack className={overrides?.frequencyAndWeeksContainer} justifyContent="start">
        <Box
          className={cn(
            'flex w-1/2 flex-row items-start space-s-8',
            overrides?.frequencyContainer,
          )}>
          <IntervalInput disabled={disabled} size={size} value={value} onChange={onChange} />
          <CustomFrequencyInput
            allowedCustomFrequencies={allowedCustomFrequencies}
            disabled={disabled}
            size={size}
            value={value}
            onChange={onChange}
          />
        </Box>
        <Box className={cn('flex w-1/2 flex-row', overrides?.weeksContainer)}>
          <Typography
            className={cn('mx-12 my-auto', { 'pb-24': Boolean(errorConfig?.weeksOfMonth) })}
            variant={size === 'medium' ? 'body-short-02' : 'body-short-01'}>
            {t('On')}
          </Typography>
          <WeeksOfMonthInput
            className="flex flex-1"
            disabled={disabled}
            error={errorConfig?.weeksOfMonth}
            size={size}
            value={{ ...value, weeksOfMonth: value.custom?.weeksOfMonth }}
            onChange={onWeekOfMonthChange}
          />
        </Box>
      </Stack>
      <Stack>
        <Box className={cn('flex flex-row items-center', hideTimeSelection ? 'w-full' : 'w-1/2')}>
          <MultiWeekday
            disabled={disabled}
            error={errorConfig?.daysOfWeek}
            size={size}
            value={value}
            onChange={onChange}
          />
        </Box>
        {hideTimeSelection ? null : (
          <TimeOfDay
            classes={{
              container: '!items-start',
              label: cn('mx-12 my-auto', { 'pb-24': Boolean(errorConfig?.daysOfWeek) }),
            }}
            disabled={disabled}
            size={size}
            value={value}
            onChange={onChange}
          />
        )}
      </Stack>
    </Stack>
  );
});

export default CustomWeek;
